/**
 * FeedPage — authenticated news feed with search, category filters,
 * pagination, article selection, export and admin toolbar.
 * Extracted from App.js (P3-2 refactor).
 */
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import {
  RefreshCw, X, CheckSquare, Search, Filter, Download, Mic, Bookmark, ChevronDown, Check, Star, Flame, ArrowDown,
  Plus, MapPin, ArrowRight,
} from "lucide-react";
import { API } from "../config";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import TickerBar from "../components/TickerBar";
import AppNav from "../components/AppNav";
import { useDarkMode } from "../hooks/useDarkMode";
import { useAdminMode } from "../hooks/useAdminMode";
import { labelFor } from "../lib/taxonomyLabels";
import { categoryGroup, GROUP_ORDER } from "../lib/taxonomyLabels";
import PublicPageFooter from "../components/PublicPageFooter";
import BriefOfWeek from "../components/BriefOfWeek";
import ArchiveModal from "../components/ArchiveModal";
import SettingsModal from "../components/SettingsModal";
import NewsCard from "../components/NewsCard";
import ProUpsellCard, { useBillingEnabled } from "../components/ProUpsellCard";
import SuggestSourceModal from "../components/SuggestSourceModal";
import ReportCard from "../components/ReportCard";
import AdCard from "../components/AdCard";
import FeedSkeleton from "../components/FeedSkeleton";
import { NewsletterModal } from "../NewsletterModal";

const FeedPage = () => {
  const { user, logout, isAdmin, authHeader } = useAuth();
  const billingEnabled = useBillingEnabled();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [darkMode] = useDarkMode();
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  const [selectedArticlesData, setSelectedArticlesData] = useState(new Map()); // id → article object, persists across pages
  const [showExportBar, setShowExportBar] = useState(false);
  const [adsense, setAdsense] = useState({ enabled: false, publisher_id: "", slot_id: "" });
  const [feedTicker, setFeedTicker] = useState(null);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalDbCount, setTotalDbCount] = useState(0);
  const [personalFilters, setPersonalFilters] = useState(false);
  const [totalReports, setTotalReports] = useState(0);
  const PAGE_SIZE = 21;
  // Search & filter
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [topCategories, setTopCategories] = useState(["All"]);
  // Redesign item 4: sort dropdown + "+ All filters" panel
  const [sortMode, setSortMode] = useState("smart");
  const sortModeRef = useRef("smart");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [pendingCategory, setPendingCategory] = useState("All");
  const sortMenuRef = useRef(null);
  const filtersAreaRef = useRef(null);
  const activeCategoryRef = useRef("All");
  const [brief, setBrief] = useState(null);
  const [addingToPodcast, setAddingToPodcast] = useState(false);
  const [briefFeaturedCount, setBriefFeaturedCount] = useState(0);
  const [addingToBrief, setAddingToBrief] = useState(false);
  const [showReddit, setShowReddit] = useState(() => {
    try { return localStorage.getItem("ti_show_reddit") === "true"; } catch { return false; }
  });
  const showRedditRef = useRef(showReddit);
  // Admin-only client-side filter: show only articles staged in any of the 3 queues
  const [stagedOnly, setStagedOnly] = useState(false);
  // Public filter: show only viral articles — SERVER-side via viral_only param
  const [viralOnly, setViralOnly] = useState(false);
  const viralOnlyRef = useRef(false);

  // Ticker dismissal persists for the session (redesign item 1)
  const [tickerDismissed, setTickerDismissed] = useState(() => {
    try { return sessionStorage.getItem("ti_ticker_dismissed") === "1"; } catch { return false; }
  });

  // Unified-nav Search: focus the feed search bar (event from AppNav or ?focus=search)
  const searchInputRef = useRef(null);
  useEffect(() => {
    const focusSearch = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => searchInputRef.current?.focus({ preventScroll: true }), 350);
    };
    if (new URLSearchParams(window.location.search).get("focus") === "search") focusSearch();
    window.addEventListener("ti-focus-search", focusSearch);
    return () => window.removeEventListener("ti-focus-search", focusSearch);
  }, []);

  // Reports (interleaved into the news grid, ~1 per 6 cards)
  const [reports, setReports] = useState([]);

  useEffect(() => { document.title = "My Feed | TourismIntel"; }, []);
  useEffect(() => { activeCategoryRef.current = activeCategory; }, [activeCategory]);
  useEffect(() => { showRedditRef.current = showReddit; }, [showReddit]);

  const fetchArticles = async (page = 1, search = "", cat = "All", reddit = null, sortOverride = null) => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE, sort: sortOverride || sortModeRef.current };
      if (search && search.trim()) params.search = search.trim();
      if (cat && cat !== "All") params.category = cat;
      const useReddit = reddit !== null ? reddit : showReddit;
      if (!useReddit) params.show_reddit = false;
      if (viralOnlyRef.current) params.viral_only = true;
      const res = await axios.get(`${API}/news`, { headers: authHeader(), params });
      const arts = res.data.articles || [];
      setArticles(arts);
      setLastUpdated(res.data.lastUpdated);
      setCurrentPage(res.data.currentPage || page);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.totalCount || 0);
      setPersonalFilters(!!res.data.personalFiltersActive);
      if (res.data.totalDbCount) setTotalDbCount(res.data.totalDbCount);
      if (!cat || cat === "All") {
        const freq = {};
        arts.forEach(a => { if (a.category) freq[a.category] = (freq[a.category] || 0) + 1; });
        const top = Object.entries(freq)
          .sort((a, b) => b[1] - a[1]).slice(0, 10).map(([c]) => c)
          .filter(c => c.toLowerCase() !== "reddit");  // Reddit handled by icon toggle
        setTopCategories(prev => {
          const merged = [...new Set([...prev.filter(c => c !== "All" && c.toLowerCase() !== "reddit"), ...top])].slice(0, 10);
          return ["All", ...merged];
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Admin mode (redesign item 2): editorial UI renders ONLY when it's ON
  const [adminMode] = useAdminMode();
  const adminUI = isAdmin && adminMode;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Admin-ribbon events: reload after RSS refresh, merge Perplexity/Discovery results
  useEffect(() => {
    const onReload = () => fetchArticles(1, searchQuery, activeCategoryRef.current, showRedditRef.current);
    const onMerge = (e) => {
      const arts = e.detail?.articles || [];
      if (!arts.length) return;
      setArticles(prev => [...prev, ...arts].filter((a, i, s) =>
        i === s.findIndex(b => (b.title || "").toLowerCase() === (a.title || "").toLowerCase())));
    };
    window.addEventListener("ti-feed-reload", onReload);
    window.addEventListener("ti-merge-articles", onMerge);
    const onRedditPref = (e) => {
      const show = !!e.detail?.show;
      setShowReddit(show);
      setCurrentPage(1);
      fetchArticles(1, searchQuery, activeCategoryRef.current, show);
    };
    window.addEventListener("ti-reddit-pref-changed", onRedditPref);
    return () => {
      window.removeEventListener("ti-feed-reload", onReload);
      window.removeEventListener("ti-merge-articles", onMerge);
      window.removeEventListener("ti-reddit-pref-changed", onRedditPref);
    };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when personal feed prefs are saved from the Settings modal (iter104)
  useEffect(() => {
    const onPrefsSaved = () => fetchArticles(1, searchQuery, activeCategoryRef.current, showRedditRef.current);
    window.addEventListener("feed-prefs-saved", onPrefsSaved);
    return () => window.removeEventListener("feed-prefs-saved", onPrefsSaved);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pull-to-refresh (redesign item 8) — touch devices only, no desktop scroll
  // hijack: passive touch listeners, indicator only, stale content stays put.
  const loadingRef = useRef(false);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  const [pullPx, setPullPx] = useState(0);
  const pullPxRef = useRef(0);
  const ptrState = useRef({ startY: 0, tracking: false });
  useEffect(() => {
    const setPull = (v) => { pullPxRef.current = v; setPullPx(v); };
    const onTouchStart = (e) => {
      if (window.scrollY > 0 || loadingRef.current) return;
      ptrState.current = { startY: e.touches[0].clientY, tracking: true };
    };
    const onTouchMove = (e) => {
      if (!ptrState.current.tracking) return;
      const dy = e.touches[0].clientY - ptrState.current.startY;
      if (window.scrollY > 0 || dy <= 0) { if (pullPxRef.current) setPull(0); return; }
      setPull(Math.min(96, dy * 0.45));
    };
    const onTouchEnd = () => {
      if (!ptrState.current.tracking) return;
      ptrState.current.tracking = false;
      const px = pullPxRef.current;
      setPull(0);
      if (px >= 60 && !loadingRef.current) {
        setCurrentPage(1);
        fetchArticles(1, searchQuery, activeCategoryRef.current, showRedditRef.current);
      }
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch reports once on mount — interleaved into the news grid below
  useEffect(() => {
    axios.get(`${API}/reports`, { params: { limit: 12 } })
      .then((res) => setReports(res.data.reports || []))
      .catch(() => setReports([]));
    // Live count for the stats bar (separate from teaser-limited list above)
    axios.get(`${API}/public-stats`, { timeout: 4000 })
      .then((r) => setTotalReports(r.data?.reports || 0))
      .catch(() => {});
  }, []);

  const goToPage = (p) => {
    const page = Math.max(1, Math.min(totalPages, p));
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchArticles(page, searchQuery, activeCategoryRef.current, showReddit);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    activeCategoryRef.current = cat;
    setCurrentPage(1);
    setSelectedArticles(new Set());
    setShowExportBar(false);
    fetchArticles(1, searchQuery, cat, showReddit);
  };

  const handleSortChange = (mode) => {
    setSortMode(mode);
    sortModeRef.current = mode;
    setSortMenuOpen(false);
    setCurrentPage(1);
    fetchArticles(1, searchQuery, activeCategoryRef.current, showRedditRef.current, mode);
  };

  // One-click reset of every user filter (item 4 count line)
  const clearAllFilters = () => {
    setViralOnly(false);
    viralOnlyRef.current = false;
    setStagedOnly(false);
    setActiveCategory("All");
    activeCategoryRef.current = "All";
    setCurrentPage(1);
    if (searchQuery) setSearchQuery(""); // debounce triggers the refetch
    else fetchArticles(1, "", "All", showRedditRef.current);
  };

  // Close sort menu / filters panel on outside click
  useEffect(() => {
    if (!sortMenuOpen && !filtersPanelOpen) return;
    const onDocClick = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) setSortMenuOpen(false);
      if (filtersAreaRef.current && !filtersAreaRef.current.contains(e.target)) setFiltersPanelOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [sortMenuOpen, filtersPanelOpen]);

  const handleArticleSelect = (article) => {
    const id = article.id || article.title;
    const nsIds = new Set(selectedArticles);
    const nsData = new Map(selectedArticlesData);
    if (nsIds.has(id)) { nsIds.delete(id); nsData.delete(id); }
    else { nsIds.add(id); nsData.set(id, article); }
    setSelectedArticles(nsIds);
    setSelectedArticlesData(nsData);
    setShowExportBar(nsIds.size > 0);
  };

  const selectAllArticles = () => {
    const nsIds = new Set(selectedArticles);
    const nsData = new Map(selectedArticlesData);
    articles.forEach(a => { const id = a.id || a.title; nsIds.add(id); nsData.set(id, a); });
    setSelectedArticles(nsIds);
    setSelectedArticlesData(nsData);
    setShowExportBar(true);
  };
  const clearSelection = () => { setSelectedArticles(new Set()); setSelectedArticlesData(new Map()); setShowExportBar(false); };
  const getSelectedArticleObjects = () => Array.from(selectedArticlesData.values());

  const exportToMarkdown = () => {
    const sel = getSelectedArticleObjects();
    if (!sel.length) { setShowExportBar(true); toast.warning("Select articles first"); return; }
    const fmt = (d) => new Date(d).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    let md = `# TourismIntel — News Export\n\n**Export Date:** ${fmt(new Date())}\n**Total Articles:** ${sel.length}\n\n---\n\n`;
    sel.forEach((a, i) => { md += `## ${i + 1}. ${a.title}\n\n**Source:** ${a.source} · **Published:** ${fmt(a.publishedAt)}\n\n**URL:** [${a.originalUrl}](${a.originalUrl})\n\n### Summary\n\n${a.summary}\n\n---\n\n`; });
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `AI-Tourism-News-Export-${new Date().toISOString().split("T")[0]}.md`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success(`Exported ${sel.length} articles`);
  };

  const addToPodcastQueue = async () => {
    const sel = getSelectedArticleObjects();
    if (!sel.length) { toast.warning("Select articles first"); return; }
    setAddingToPodcast(true);
    // Auto-stage for brief priority (silent — don't show extra toast)
    addToBriefFeatured(true);
    let added = 0; let skipped = 0;
    for (const article of sel) {
      try {
        await axios.post(`${API}/admin/podcast/queue/${article.id}`, {}, { headers: authHeader() });
        added++;
      } catch (err) {
        if (err.response?.status === 200 || err.response?.data?.message?.includes("Already")) { skipped++; }
        else { skipped++; }
      }
    }
    setAddingToPodcast(false);
    if (added > 0) toast.success(`${added} article${added !== 1 ? "s" : ""} added to Podcast queue${skipped ? ` (${skipped} already queued)` : ""}`);
    else toast.info("Articles already in Podcast queue");
  };

  // ── Brief featured articles ────────────────────────────────────────────────
  const addToBriefFeatured = async (silent = false) => {
    const sel = getSelectedArticleObjects();
    if (!sel.length) return;
    setAddingToBrief(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/brief/featured`,
        { articles: sel.map(a => ({ id: a.id, title: a.title, summary: a.summary, source: a.source, originalUrl: a.originalUrl })) },
        { headers: authHeader() }
      );
      setBriefFeaturedCount(data.total || 0);
      if (!silent) toast.success(`${sel.length} article${sel.length !== 1 ? "s" : ""} added to brief priority (${data.total} total staged)`);
    } catch { if (!silent) toast.error("Could not stage articles for brief"); }
    finally { setAddingToBrief(false); }
  };

  // Load current featured count on mount
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/admin/brief/featured`, { headers: authHeader() })
      .then(r => setBriefFeaturedCount(r.data.count || 0))
      .catch(() => {});
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Single-article star handlers (per-card inline action) ──────────────────
  // Optimistic local update so the gold-star indicator flips immediately.
  const _patchArticle = (id, patch) => {
    setArticles(prev => prev.map(a => (a.id === id ? { ...a, ...patch } : a)));
  };

  // ── Layer A: ranking feedback (promote/demote/clear) — optimistic ──────────
  // Delete requires the confirmation dialog (redesign item 2) — no window.confirm.
  const requestDelete = (article) => setDeleteTarget(article);

  const confirmDelete = async () => {
    const article = deleteTarget;
    if (!article) return;
    setDeleting(true);
    try {
      const { data } = await axios.delete(`${API}/admin/news/${article.id}`, { headers: authHeader() });
      setArticles(prev => prev.filter(a => a.id !== article.id));
      toast.success(`Story deleted${data.label_cleared ? " (ranking label cleared)" : ""}`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // iter182: admin PR override («non è PR» / «segna come PR») — detector never re-flags an overridden article
  const prOverride = async (article, isPr) => {
    const prev = { is_pr: article.is_pr, pr_override: article.pr_override };
    _patchArticle(article.id, { is_pr: isPr, pr_override: { is_pr: isPr, by: user?.email, at: new Date().toISOString() } });
    try {
      const { data } = await axios.post(`${API}/admin/news/${article.id}/pr-override`, { is_pr: isPr }, { headers: authHeader() });
      _patchArticle(article.id, { is_pr: data.is_pr, pr_override: data.pr_override });
      toast.success(isPr ? "Segnato come comunicato stampa (rilevanza minima)" : "Override salvato: non è PR — rilevanza normale");
    } catch (e) {
      _patchArticle(article.id, prev);
      toast.error(e?.response?.data?.detail || "Override PR fallito");
    }
  };

  const rankFeedback = async (article, action) => {
    const prev = article.rank_override || null;
    const optimistic = action === "clear" ? null : { action, by: user?.email, at: new Date().toISOString() };
    _patchArticle(article.id, { rank_override: optimistic });
    try {
      const { data } = await axios.post(
        `${API}/admin/ranking-feedback`,
        { target_id: article.id, action },
        { headers: authHeader() }
      );
      if (data.override) {
        _patchArticle(article.id, { rank_override: {
          action: data.override.action, multiplier: data.override.multiplier,
          by: data.override.created_by, at: data.override.created_at,
          expires_at: data.override.expires_at,
        }});
      }
      if (action === "clear") {
        toast.success("Ranking restored to neutral");
      } else {
        toast.success(action === "promote" ? "Story promoted in the feed (72h)" : "Story demoted in the feed (72h)", {
          action: { label: "Undo", onClick: () => rankFeedback(article, "clear") },
        });
      }
    } catch {
      _patchArticle(article.id, { rank_override: prev });
      toast.error("Could not save ranking feedback");
    }
  };

  const starToBrief = async (article) => {
    _patchArticle(article.id, { in_brief_queue: true });
    try {
      const { data } = await axios.post(
        `${API}/admin/brief/featured`,
        { articles: [{ id: article.id, title: article.title, summary: article.summary, source: article.source, originalUrl: article.originalUrl }] },
        { headers: authHeader() }
      );
      setBriefFeaturedCount(data.total || 0);
      toast.success(`"${article.title?.slice(0, 50)}…" added to Brief queue`, {
        action: { label: "Undo", onClick: () => unstarBrief(article) },
      });
    } catch {
      _patchArticle(article.id, { in_brief_queue: false });
      toast.error("Could not add to Brief");
    }
  };

  const starToNewsletter = async (article) => {
    _patchArticle(article.id, { in_newsletter_queue: true });
    try {
      await axios.post(
        `${API}/admin/newsletter/featured`,
        { articles: [{ id: article.id, title: article.title, summary: article.summary, source: article.source, originalUrl: article.originalUrl }] },
        { headers: authHeader() }
      );
      toast.success(`"${article.title?.slice(0, 50)}…" added to Newsletter queue`, {
        action: { label: "Undo", onClick: () => unstarNewsletter(article) },
      });
    } catch {
      _patchArticle(article.id, { in_newsletter_queue: false });
      toast.error("Could not add to Newsletter");
    }
  };

  const starToPodcast = async (article) => {
    _patchArticle(article.id, { in_podcast_queue: true });
    try {
      await axios.post(`${API}/admin/podcast/queue/${article.id}`, {}, { headers: authHeader() });
      toast.success(`"${article.title?.slice(0, 50)}…" added to Podcast queue`, {
        action: { label: "Undo", onClick: () => unstarPodcast(article) },
      });
    } catch (err) {
      if (err.response?.data?.message?.includes("Already")) {
        toast.info(`"${article.title?.slice(0, 50)}…" already in Podcast queue`);
      } else {
        _patchArticle(article.id, { in_podcast_queue: false });
        toast.error("Could not add to Podcast");
      }
    }
  };

  // ── Unstar handlers — DELETE from queue, with optimistic flip ──────────────
  const unstarBrief = async (article) => {
    _patchArticle(article.id, { in_brief_queue: false });
    try {
      await axios.delete(`${API}/admin/brief/featured/${encodeURIComponent(article.id)}`, { headers: authHeader() });
      setBriefFeaturedCount(prev => Math.max(0, prev - 1));
      toast.success(`Removed from Brief queue`);
    } catch {
      _patchArticle(article.id, { in_brief_queue: true });
      toast.error("Could not remove from Brief");
    }
  };

  const unstarNewsletter = async (article) => {
    _patchArticle(article.id, { in_newsletter_queue: false });
    try {
      await axios.delete(`${API}/admin/newsletter/featured/${encodeURIComponent(article.id)}`, { headers: authHeader() });
      toast.success(`Removed from Newsletter queue`);
    } catch {
      _patchArticle(article.id, { in_newsletter_queue: true });
      toast.error("Could not remove from Newsletter");
    }
  };

  const unstarPodcast = async (article) => {
    _patchArticle(article.id, { in_podcast_queue: false });
    try {
      await axios.delete(`${API}/admin/podcast/queue/${article.id}`, { headers: authHeader() });
      toast.success(`Removed from Podcast queue`);
    } catch {
      _patchArticle(article.id, { in_podcast_queue: true });
      toast.error("Could not remove from Podcast");
    }
  };

   
  useEffect(() => {
    fetchArticles(1, "", "All", showReddit);
    axios.get(`${API}/news/categories`, { headers: authHeader() })
      .then(r => {
        if (r.data.categories?.length) {
          // Exclude "REDDIT" — handled separately by the icon toggle
          setTopCategories(r.data.categories.filter(c => c.toLowerCase() !== "reddit"));
        }
      })
      .catch(() => {});
    axios.get(`${API}/brief`).then(r => setBrief(r.data.brief)).catch(() => {});
    axios.get(`${API}/ticker`).then(r => setFeedTicker(r.data)).catch(() => {});
    axios.get(`${API}/adsense-config`).then(r => {
      setAdsense(r.data);
      if (r.data.enabled && r.data.publisher_id && !document.querySelector(`script[src*="adsbygoogle"]`)) {
        const s = document.createElement("script");
        s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${r.data.publisher_id}`;
        s.async = true; s.crossOrigin = "anonymous";
        document.head.appendChild(s);
      }
    }).catch(() => {});
  }, []);

  // Debounced search
  const _searchTimer = useRef(null);
  useEffect(() => {
    if (_searchTimer.current) clearTimeout(_searchTimer.current);
    _searchTimer.current = setTimeout(() => {
      setCurrentPage(1);
      fetchArticles(1, searchQuery, activeCategoryRef.current, showRedditRef.current);
    }, 450);
    return () => clearTimeout(_searchTimer.current);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const dm = darkMode;
  const feedTickerVisible = feedTicker?.is_enabled && feedTicker?.items?.length > 0 && !tickerDismissed;
  const filteredArticles = (() => {
    let list = articles;
    if (stagedOnly && adminUI) {
      list = list.filter(a => a.in_brief_queue || a.in_newsletter_queue || a.in_podcast_queue);
    }
    return list;
  })();
  const categories = topCategories;
  const visibleChips = categories.slice(0, 10);
  const groupedCategories = (() => {
    const groups = {};
    categories.filter(c => c !== "All").forEach(c => {
      const g = categoryGroup(c);
      (groups[g] = groups[g] || []).push(c);
    });
    return GROUP_ORDER.filter(g => groups[g]).map(g => [g, groups[g]]);
  })();
  const hasActiveFilters = !!searchQuery.trim() || activeCategory !== "All" || viralOnly || (stagedOnly && adminUI);
  // Named filters for the empty state headline (redesign item 8)
  const activeFilterNames = [
    searchQuery.trim() ? `"${searchQuery.trim()}"` : null,
    activeCategory !== "All" ? labelFor(activeCategory) : null,
    viralOnly ? "Viral" : null,
  ].filter(Boolean);
  const SUGGESTED_TOPICS = ["Hotel Industry", "AI in travel", "Aviation"];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"}`}>
      <TickerBar
        config={feedTicker}
        darkMode={dm}
        dismissed={tickerDismissed}
        onDismiss={() => {
          setTickerDismissed(true);
          try { sessionStorage.setItem("ti_ticker_dismissed", "1"); } catch {}
        }}
      />

      <AppNav active="stories" darkMode={dm} tickerVisible={feedTickerVisible} />

      {/* Pull-to-refresh indicator — appears only while pulling (touch) */}
      {pullPx > 0 && (
        <div
          data-testid="pull-to-refresh-indicator"
          className="fixed left-1/2 z-40 pointer-events-none"
          style={{ top: 10 + pullPx * 0.6, transform: "translateX(-50%)", opacity: Math.min(1, pullPx / 48) }}
        >
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${dm ? "bg-[#1a2122]" : "bg-white"}`}
            style={{ boxShadow: "0 4px 16px rgba(16,40,44,0.18)" }}>
            <ArrowDown className="w-4 h-4"
              style={{
                color: pullPx >= 60 ? (dm ? "#22d3ee" : "#006874") : "#9aa7a9",
                transform: `rotate(${Math.min(180, pullPx * 3)}deg)`,
                transition: "color 120ms",
              }} />
          </div>
        </div>
      )}

      {/* "Updating…" pill — refresh keeps stale content visible (redesign item 8) */}
      {loading && articles.length > 0 && (
        <div data-testid="feed-updating-pill" className="fixed left-1/2 z-40 pointer-events-none"
          style={{ top: feedTickerVisible ? 100 : 68, transform: "translateX(-50%)" }}>
          <span className={`flex items-center gap-2 px-4 py-1.5 rounded-full category-tag ${dm ? "bg-[#0f2529] text-[#8fd4dc]" : "bg-[#e0eef0] text-[#006874]"}`}
            style={{ boxShadow: "0 6px 20px rgba(16,40,44,0.16)" }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "currentColor" }} />
            Updating…
          </span>
        </div>
      )}

      <main className="max-w-screen-xl mx-auto px-6 pt-10 pb-20">
        {/* Stats bar */}
        <div className={`rounded-2xl p-5 mb-8 flex items-center justify-between flex-wrap gap-4 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full pulse-dot ${dm ? "bg-cyan-400" : "bg-[#006874]"}`} />
              <span className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{totalDbCount > 0 ? totalDbCount : (totalCount > 0 ? totalCount : articles.length)}</span>
              <span className={`category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Articles</span>
            </div>
            {totalReports > 0 && (
              <Link
                to="/reports"
                data-testid="reports-count-link"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                title="Browse Reports & Research"
              >
                <span className={`w-2 h-2 rounded-full pulse-dot ${dm ? "bg-emerald-400" : "bg-emerald-600"}`} />
                <span className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{totalReports}</span>
                <span className={`category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Reports &amp; Research</span>
              </Link>
            )}
            {lastUpdated && <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Updated {new Date(lastUpdated).toLocaleString()}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {["Auto-refreshes every 12h", "AI Summaries", "3-Month Archive"].map(t => (
              <span key={t} className={`px-3 py-1 rounded-full category-tag ${dm ? "bg-[#252e2f] text-slate-500" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>{t}</span>
            ))}
          </div>
        </div>

        {/* Export bar (admin only) */}
        {adminUI && showExportBar && (
          <div className={`rounded-2xl p-4 mb-6 sticky top-16 z-40 flex items-center justify-between flex-wrap gap-3 ${dm ? "bg-[#1a2122]/95 backdrop-blur-md" : "bg-white/95 backdrop-blur-md"}`} style={{ boxShadow: "0 0 0 1px rgba(0,104,116,0.2), 0 4px 24px rgba(0,104,116,0.08)" }}>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><CheckSquare className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} /><span className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{selectedArticles.size} Article{selectedArticles.size !== 1 ? "s" : ""} Selected</span></div>
              <button onClick={selectAllArticles} className={`category-tag ${dm ? "text-cyan-400" : "text-[#006874]"}`}>+ This page</button>
              <button onClick={clearSelection} className={`category-tag ${dm ? "text-slate-500 hover:text-red-400" : "text-[#6b7a7d] hover:text-red-500"}`}>Clear all</button>
            </div>
            <div className="flex gap-2">
              <NewsletterModal
                selectedArticles={selectedArticles}
                getSelectedArticleObjects={getSelectedArticleObjects}
                darkMode={dm}
                disabled={false}
                onSend={() => addToBriefFeatured(true)}
              />
              <button
                onClick={addToPodcastQueue}
                disabled={addingToPodcast}
                data-testid="add-to-podcast-btn"
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-50 ${dm ? "bg-purple-400/15 text-purple-300 hover:bg-purple-400/25 border border-purple-400/30" : "bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"}`}
              >
                {addingToPodcast ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
                Podcast {selectedArticles.size > 0 && `(${selectedArticles.size})`}
              </button>
              <button
                onClick={() => addToBriefFeatured(false)}
                disabled={addingToBrief}
                data-testid="add-to-brief-btn"
                title={briefFeaturedCount > 0 ? `${briefFeaturedCount} articles staged for next brief — click to add more` : "Stage selected articles as high-priority for next brief"}
                className={`relative flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-50 ${dm ? "bg-amber-400/15 text-amber-300 hover:bg-amber-400/25 border border-amber-400/30" : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"}`}
              >
                {addingToBrief ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" />}
                Brief {selectedArticles.size > 0 && `(${selectedArticles.size})`}
                {briefFeaturedCount > 0 && (
                  <span className={`absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${dm ? "bg-amber-400 text-[#001f24]" : "bg-amber-500 text-white"}`}>
                    {briefFeaturedCount}
                  </span>
                )}
              </button>
              <button onClick={exportToMarkdown} data-testid="export-md-btn" className={`flex items-center gap-2 px-5 py-2 rounded-full category-tag transition-all ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
                <Download className="w-3.5 h-3.5" />Export .md
              </button>
            </div>
          </div>
        )}

        <BriefOfWeek brief={brief} darkMode={dm} />

        {/* Section heading + Search/Filter */}
        <div className="mb-6">
          <div className="mb-3 md:mb-4">
            <p className={`hidden md:block category-tag mb-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Personalized Feed</p>
            <h2 className={`editorial-heading text-xl md:text-4xl font-medium tracking-tight ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Latest in Travel-Tech, Innovation, AI in Travel &amp; Tourism</h2>
          </div>

          {/* Search + sort (redesign item 4) */}
          <div
            className={`flex items-center rounded-xl mb-3 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
            style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12)", minHeight: 44 }}
          >
            <div className="flex items-center gap-2 px-4 py-2.5 flex-1 min-w-0">
              <Search className={`w-4 h-4 flex-shrink-0 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} />
              <input
                data-testid="feed-search-input"
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search 3-month story archive — titles, summaries, sources…"
                className={`flex-1 bg-transparent text-sm outline-none min-w-0 ${dm ? "text-slate-200 placeholder:text-slate-600" : "text-[#191c1d] placeholder:text-[#bac9cc]"}`}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className={`flex-shrink-0 ${dm ? "text-slate-600 hover:text-slate-400" : "text-[#bac9cc] hover:text-[#6b7a7d]"}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className={`w-px self-stretch my-2 flex-shrink-0 ${dm ? "bg-white/8" : "bg-black/8"}`} />
            <div className="relative flex-shrink-0 self-stretch" ref={sortMenuRef}>
              <button
                data-testid="feed-sort-btn"
                onClick={() => setSortMenuOpen(o => !o)}
                aria-expanded={sortMenuOpen}
                className={`h-full flex items-center gap-1.5 px-4 category-tag transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006874] ${dm ? "text-slate-400 hover:text-slate-200" : "text-[#5b6b70] hover:text-[#191c1d]"}`}
              >
                <span className="hidden sm:inline">Sort:&nbsp;</span>{sortMode === "smart" ? "Relevance" : "Newest"}
                <ChevronDown className={`w-3 h-3 transition-transform ${sortMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {sortMenuOpen && (
                <div
                  data-testid="feed-sort-menu"
                  className={`absolute right-0 top-full mt-1 w-48 rounded-xl overflow-hidden z-40 animate-fade-in ${dm ? "bg-[#252e2f] border border-white/5" : "bg-white border border-black/5"}`}
                  style={{ boxShadow: "0 14px 32px rgba(16,40,44,0.14)" }}
                >
                  {[
                    { v: "smart", label: "Relevance", desc: "Ranking score (default)" },
                    { v: "recent", label: "Newest", desc: "Most recent first" },
                  ].map(o => (
                    <button
                      key={o.v}
                      data-testid={`sort-option-${o.v}`}
                      onClick={() => handleSortChange(o.v)}
                      className={`w-full flex items-start gap-2 px-4 py-2.5 text-left transition-colors ${dm ? "hover:bg-white/5" : "hover:bg-[#f8f9fa]"}`}
                    >
                      <span className="flex-1">
                        <span className={`block text-xs font-semibold ${sortMode === o.v ? (dm ? "text-cyan-400" : "text-[#006874]") : (dm ? "text-slate-200" : "text-[#191c1d]")}`}>{o.label}</span>
                        <span className={`block text-[11px] mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{o.desc}</span>
                      </span>
                      {sortMode === o.v && <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Category chips — wrap onto rows, never horizontal scroll (item 4) */}
          <div ref={filtersAreaRef} className="relative mb-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {/* Viral-only toggle — server-side filter across the whole feed */}
              <button
                onClick={() => {
                  const next = !viralOnly;
                  setViralOnly(next);
                  viralOnlyRef.current = next;
                  setCurrentPage(1);
                  fetchArticles(1, searchQuery, activeCategoryRef.current, showRedditRef.current);
                }}
                title={viralOnly ? "Showing only viral stories — click to show all" : "Show only viral stories (covered by 3+ publishers)"}
                data-testid="viral-only-toggle-btn"
                className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006874] ${
                  viralOnly
                    ? (dm ? "bg-[#b3541e]/25 text-orange-300" : "bg-[#faeee6] text-[#b3541e]")
                    : `opacity-40 hover:opacity-80 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`
                }`}
              >
                <Flame className={`w-4 h-4 ${viralOnly ? "fill-current" : ""}`} />
              </button>
              {/* Staged-only toggle (admin mode) */}
              {adminUI && (
                <button
                  onClick={() => setStagedOnly(s => !s)}
                  title={stagedOnly ? "Showing only staged articles — click to show all" : "Show only articles staged in Brief/Newsletter/Podcast"}
                  data-testid="staged-only-toggle-btn"
                  className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d8a940] ${
                    stagedOnly
                      ? (dm ? "bg-amber-400/20 text-amber-300" : "bg-amber-100 text-amber-600")
                      : `opacity-40 hover:opacity-80 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`
                  }`}
                >
                  <Star className={`w-4 h-4 ${stagedOnly ? "fill-current" : ""}`} />
                </button>
              )}
              {/* Reddit toggle */}
              <button
                onClick={() => {
                  const next = !showReddit;
                  setShowReddit(next);
                  try { localStorage.setItem("ti_show_reddit", String(next)); } catch {}
                  let cat = activeCategoryRef.current;
                  if (!next && cat === "Reddit") {
                    // hiding Reddit while filtered on it would show an empty,
                    // contradictory feed — reset to All
                    cat = "All";
                    setActiveCategory("All");
                    activeCategoryRef.current = "All";
                  }
                  setCurrentPage(1);
                  fetchArticles(1, searchQuery, cat, next);
                }}
                title={showReddit ? "Reddit ON — click to hide" : "Reddit OFF — click to show"}
                data-testid="reddit-toggle-btn"
                className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006874] ${showReddit ? "opacity-100" : "opacity-30 hover:opacity-60"}`}
              >
                <svg viewBox="0 0 20 20" className="w-5 h-5" fill="currentColor">
                  <circle cx="10" cy="10" r="10" fill={showReddit ? "#FF4500" : (dm ? "#4b5563" : "#9ca3af")} />
                  <path fill="white" d="M16.67 10a1.46 1.46 0 0 0-2.47-1 7.12 7.12 0 0 0-3.85-1.23l.65-3.08 2.13.45a1 1 0 1 0 .14-.5l-2.38-.5a.25.25 0 0 0-.3.19l-.73 3.44a7.14 7.14 0 0 0-3.89 1.23 1.46 1.46 0 1 0-1.61 2.39 2.87 2.87 0 0 0 0 .44c0 2.24 2.61 4.06 5.83 4.06s5.83-1.82 5.83-4.06a2.87 2.87 0 0 0 0-.44 1.46 1.46 0 0 0 .56-1.39zM7.27 11a1 1 0 1 1 1 1 1 1 0 0 1-1-1zm5.58 2.65a3.56 3.56 0 0 1-2.85.86 3.56 3.56 0 0 1-2.85-.86.25.25 0 0 1 .35-.35 3.07 3.07 0 0 0 2.5.71 3.07 3.07 0 0 0 2.5-.71.25.25 0 0 1 .35.35zm-.13-1.65a1 1 0 1 1 1-1 1 1 0 0 1-1 1z" />
                </svg>
              </button>
              <div className={`w-px h-5 flex-shrink-0 ${dm ? "bg-white/8" : "bg-black/8"}`} />

              {/* Reddit-only chip — visible only while Reddit sources are ON,
                  so community stories don't get lost in the ranked feed */}
              {showReddit && (
                <button
                  data-testid="category-filter-reddit"
                  onClick={() => handleCategoryChange(activeCategory === "Reddit" ? "All" : "Reddit")}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full category-tag transition-all whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF4500] ${
                    activeCategory === "Reddit"
                      ? (dm ? "bg-[#FF4500]/20 text-orange-300 border border-orange-400/60" : "bg-[#fff1ec] text-[#cc3700] border border-[#FF4500]")
                      : (dm ? "bg-[#1a2122] text-slate-400 border border-transparent hover:bg-[#252e2f] hover:text-orange-300" : "bg-[#eef1f2] text-[#5b6b70] border border-transparent hover:bg-[#fff1ec] hover:text-[#cc3700]")
                  }`}
                >
                  Reddit
                  {activeCategory === "Reddit" && <X className="w-3 h-3" />}
                </button>
              )}

              {visibleChips.map(cat => {
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    data-testid={`category-filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => handleCategoryChange(isActive && cat !== "All" ? "All" : cat)}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full category-tag transition-all whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006874] ${
                      isActive
                        ? (dm ? "bg-[#0f2529] text-cyan-300 border border-cyan-400/60" : "bg-white text-[#006874] border border-[#006874]")
                        : (dm ? "bg-[#1a2122] text-slate-400 border border-transparent hover:bg-[#252e2f] hover:text-slate-200" : "bg-[#eef1f2] text-[#5b6b70] border border-transparent hover:bg-[#f0f5f5] hover:text-[#006874]")
                    }`}
                  >
                    {labelFor(cat)}
                    {isActive && cat !== "All" && <X className="w-3 h-3" />}
                  </button>
                );
              })}
              {/* Category selected from the panel but outside the visible top-9 */}
              {activeCategory !== "All" && activeCategory !== "Reddit" && !visibleChips.includes(activeCategory) && (
                <button
                  data-testid={`category-filter-${activeCategory.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => handleCategoryChange("All")}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full category-tag transition-all whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006874] ${dm ? "bg-[#0f2529] text-cyan-300 border border-cyan-400/60" : "bg-white text-[#006874] border border-[#006874]"}`}
                >
                  {labelFor(activeCategory)}
                  <X className="w-3 h-3" />
                </button>
              )}

              <button
                data-testid="all-filters-btn"
                onClick={() => { setPendingCategory(activeCategory); setFiltersPanelOpen(o => !o); }}
                aria-expanded={filtersPanelOpen}
                className={`flex items-center gap-1 px-3 py-1 rounded-full category-tag border border-dashed transition-all whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006874] ${dm ? "border-slate-600 text-slate-400 hover:text-slate-200 hover:border-slate-400" : "border-[#c9d6d9] text-[#5b6b70] hover:text-[#006874] hover:border-[#006874]"}`}
              >
                <Filter className="w-3 h-3" />+ All filters
              </button>
            </div>

            {/* "+ All filters" panel — grouped taxonomy, internal scroll, no page scroll-lock */}
            {filtersPanelOpen && (
              <div
                data-testid="all-filters-panel"
                className={`absolute left-0 top-full mt-2 w-full sm:max-w-[560px] rounded-xl z-40 overflow-hidden animate-fade-in ${dm ? "bg-[#252e2f] border border-white/5" : "bg-white border border-black/5"}`}
                style={{ boxShadow: "0 14px 32px rgba(16,40,44,0.14)" }}
              >
                <div className="overflow-y-auto p-4" style={{ maxHeight: 320 }}>
                  {groupedCategories.map(([group, cats]) => (
                    <div key={group} className="mb-3 last:mb-0">
                      <p className={`category-tag mb-1.5 ${dm ? "text-slate-500" : "text-[#96a4a8]"}`}>{group}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        {cats.map(cat => (
                          <label key={cat} className={`flex items-center gap-2 py-1 cursor-pointer text-xs ${dm ? "text-slate-300" : "text-[#37464c]"}`}>
                            <input
                              type="checkbox"
                              data-testid={`filter-checkbox-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                              checked={pendingCategory === cat}
                              onChange={(e) => setPendingCategory(e.target.checked ? cat : "All")}
                              className="w-3.5 h-3.5 accent-[#006874] rounded"
                            />
                            {labelFor(cat)}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`flex items-center justify-between gap-3 px-4 py-3 border-t ${dm ? "border-white/5" : "border-black/5"}`}>
                  <p className={`text-[11px] ${dm ? "text-slate-500" : "text-[#96a4a8]"}`}>One topic at a time — picking a new one replaces the current filter.</p>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      data-testid="all-filters-clear-btn"
                      onClick={() => setPendingCategory("All")}
                      className={`px-4 py-1.5 rounded-full category-tag transition-colors ${dm ? "bg-[#1a2122] text-slate-300 hover:bg-[#303b3c]" : "bg-[#eef1f2] text-[#5b6b70] hover:bg-[#e2e7e8]"}`}
                    >
                      Clear
                    </button>
                    <button
                      data-testid="all-filters-apply-btn"
                      onClick={() => { handleCategoryChange(pendingCategory); setFiltersPanelOpen(false); }}
                      className={`px-4 py-1.5 rounded-full category-tag text-white transition-colors ${dm ? "bg-cyan-500 hover:bg-cyan-400 text-[#001f24]" : "bg-[#006874] hover:bg-[#00525c]"}`}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Result count line — extended (item 4) */}
          <p className={`category-tag flex items-center flex-wrap gap-x-2 gap-y-1 ${dm ? "text-slate-600" : "text-[#96a4a8]"}`} data-testid="feed-results-count">
            {searchQuery.trim() ? (
              <span>
                {loading
                  ? "Searching 3-month archive…"
                  : `${totalCount} ${totalCount === 1 ? "result" : "results"} for "${searchQuery.trim()}" · 3-month archive`}
              </span>
            ) : (
              <>
                <span>
                  {totalDbCount > totalCount
                    ? `${totalCount.toLocaleString("en-US")} of ${totalDbCount.toLocaleString("en-US")} stories`
                    : `${totalCount.toLocaleString("en-US")} ${totalCount === 1 ? "story" : "stories"}`}
                </span>
                {activeCategory !== "All" && <span>· {labelFor(activeCategory)}</span>}
                {viralOnly && <span>· Viral only</span>}
                {stagedOnly && adminUI && <span>· Staged only</span>}
                {!showReddit && <span>· Reddit hidden</span>}
                {personalFilters && <span>· Personal topic filters on</span>}
                {totalPages > 1 && <span>· page {currentPage} of {totalPages}</span>}
              </>
            )}
            {hasActiveFilters && (
              <button
                data-testid="clear-filters-btn"
                onClick={clearAllFilters}
                className={`underline underline-offset-2 font-bold transition-colors ${dm ? "text-cyan-400 hover:text-cyan-300" : "text-[#006874] hover:text-[#00525c]"}`}
              >
                Clear filters
              </button>
            )}
          </p>
        </div>

        {/* Article grid */}
        {loading && articles.length === 0 ? (
          <FeedSkeleton dm={dm} />
        ) : filteredArticles.length === 0 ? (
          <div data-testid="feed-empty-state" className={`rounded-2xl p-16 text-center ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.08)" }}>
            <Search className={`w-8 h-8 mx-auto mb-3 opacity-30 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} />
            <p className={`editorial-heading text-xl font-medium mb-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              {stagedOnly && adminUI
                ? "No staged articles in this page"
                : activeFilterNames.length > 0
                  ? `No stories match ${activeFilterNames.join(" + ")}`
                  : "No stories right now"}
            </p>
            <p className={`category-tag mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
              {stagedOnly && adminUI
                ? "Star articles to queue them for Brief / Newsletter / Podcast"
                : activeFilterNames.length > 0
                  ? "Try different filters, or clear them to see everything"
                  : "The feed refreshes every few hours — start from a topic meanwhile"}
            </p>
            {stagedOnly && adminUI ? (
              <button data-testid="empty-state-clear-btn" onClick={clearAllFilters} className={`px-5 py-2 rounded-full category-tag transition-all ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}>
                Clear filters
              </button>
            ) : activeFilterNames.length > 0 ? (
              <button data-testid="empty-state-clear-btn" onClick={clearAllFilters} className={`px-5 py-2 rounded-full category-tag transition-all ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}>
                Clear filters
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 flex-wrap" data-testid="empty-state-suggested-topics">
                {SUGGESTED_TOPICS.map(t => (
                  <button key={t} data-testid={`suggested-topic-${t.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => handleCategoryChange(t)}
                    className={`px-4 py-1.5 rounded-full category-tag transition-all ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}>
                    {labelFor(t)}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-6">
              <SuggestSourceModal darkMode={dm} trigger={
                <button type="button" data-testid="empty-state-suggest-source-btn"
                  className={`category-tag transition-colors ${dm ? "text-slate-500 hover:text-cyan-400" : "text-[#9aa7a9] hover:text-[#006874]"}`}>
                  Missing a source? Suggest it →
                </button>
              } />
            </div>
          </div>
        ) : (
          (() => {
            // Interleave reports into the news grid: 1 report every 6 cards.
            let reportIdx = 0;
            const gridItems = filteredArticles.flatMap((a, i) => {
              const card = <NewsCard key={`${a.id || "art"}-${i}`} article={a} darkMode={dm} isSelected={selectedArticles.has(a.id || a.title)} onSelect={handleArticleSelect} isAdmin={adminUI}
                onStarToBrief={starToBrief} onStarToNewsletter={starToNewsletter} onStarToPodcast={starToPodcast}
                onUnstarBrief={unstarBrief} onUnstarNewsletter={unstarNewsletter} onUnstarPodcast={unstarPodcast}
                onRankFeedback={rankFeedback} onDelete={adminUI ? requestDelete : undefined} onPrOverride={adminUI ? prOverride : undefined} />;
              const extras = [card];
              // Inject a report after every 5th news card (positions 5, 11, 17, …)
              if (reports.length > 0 && (i + 1) % 6 === 5 && reportIdx < reports.length) {
                extras.push(
                  <ReportCard key={`report-${reports[reportIdx].id}`} report={reports[reportIdx]} darkMode={dm} compact />
                );
                reportIdx += 1;
              }
              if (adsense.enabled && (i + 1) % 6 === 0) {
                extras.push(<AdCard key={`ad-${i}`} publisherId={adsense.publisher_id} slotId={adsense.slot_id} darkMode={dm} />);
              }
              return extras;
            });
            // One inline PRO upsell for non-PRO users (billing kill-switch aware).
            // Inserted at a multiple-of-6 index = row boundary on BOTH md (2-col)
            // and lg (3-col) grids — a mid-row col-span item would strand up to
            // 2 empty cells before it (the "holes in the feed" bug, iter85b).
            // Placeholder math below intentionally ignores it: it fills its own row.
            const cellCount = gridItems.length;
            if (billingEnabled === true && !user?.is_pro && cellCount >= 6) {
              const upsellIdx = Math.min(12, Math.floor(cellCount / 6) * 6);
              gridItems.splice(upsellIdx, 0,
                <div key="pro-upsell-feed" className="md:col-span-2 lg:col-span-3">
                  <ProUpsellCard surface="feed_limit" darkMode={dm} />
                </div>
              );
            }
            const remainder = cellCount % 3;
            // Leftover grid cells become useful mini-CTAs instead of decorative
            // ghosts: source suggestion (community input) + Insights discovery.
            const PLACEHOLDER_CTAS = [
              {
                key: "suggest-source",
                modal: true,
                Icon: Plus,
                title: "Suggest a source",
                text: "Know a great travel-industry source we should track? Tell us.",
                action: "Open form",
              },
              {
                key: "insights",
                href: "/insights",
                Icon: MapPin,
                title: "Demand Insights",
                badge: "BETA",
                text: "Weather, holidays & local events around your property — see what drives demand.",
                action: "Open",
              },
            ];
            const placeholders = remainder === 0 ? [] : Array.from({ length: 3 - remainder }, (_, i) => {
              const cta = PLACEHOLDER_CTAS[i % PLACEHOLDER_CTAS.length];
              const inner = (
                <>
                  <cta.Icon className={`w-4 h-4 mb-2 ${dm ? "text-cyan-400/70" : "text-[#006874]/70"}`} />
                  <p className={`text-sm font-semibold flex items-center gap-1.5 ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>
                    {cta.title}
                    {cta.badge && <span className={`text-[8px] font-bold tracking-wider px-1.5 py-0.5 rounded-full ${dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"}`}>{cta.badge}</span>}
                  </p>
                  <p className={`text-xs mt-1 ${dm ? "text-slate-500" : "text-[#9aa7a9]"}`}>{cta.text}</p>
                  <span className={`text-[10px] font-bold tracking-wider uppercase mt-3 inline-flex items-center gap-1 ${dm ? "text-cyan-400" : "text-[#006874]"}`}>
                    {cta.action} <ArrowRight className="w-3 h-3" />
                  </span>
                </>
              );
              const cls = `rounded-2xl p-5 flex flex-col justify-center transition-opacity opacity-60 hover:opacity-100 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
              const style = { minHeight: 180, boxShadow: "0 0 0 1px rgba(186,201,204,0.12)" };
              return cta.modal ? (
                <SuggestSourceModal key={cta.key} darkMode={dm} trigger={
                  <button type="button" data-testid={`feed-placeholder-${cta.key}`} className={`${cls} text-left w-full`} style={style}>{inner}</button>
                } />
              ) : (
                <Link key={cta.key} to={cta.href} data-testid={`feed-placeholder-${cta.key}`} className={cls} style={style}>{inner}</Link>
              );
            });
            return (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {gridItems}
                {placeholders}
              </div>
            );
          })()
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10" data-testid="pagination-controls">
            <button data-testid="pagination-prev" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-30 disabled:cursor-not-allowed ${dm ? "bg-[#1a2122] text-slate-300 hover:bg-[#252e2f]" : "bg-white text-[#3b494c] hover:bg-[#f3f4f5]"}`}
              style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12)" }}>← Prev</button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push("..."); acc.push(p); return acc; }, [])
              .map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className={`category-tag px-1 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>…</span>
                ) : (
                  <button key={p} data-testid={`pagination-page-${p}`} onClick={() => goToPage(p)}
                    className={`w-9 h-9 rounded-full category-tag font-medium transition-all ${p === currentPage ? (dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white") : (dm ? "bg-[#1a2122] text-slate-400 hover:bg-[#252e2f]" : "bg-white text-[#6b7a7d] hover:bg-[#f3f4f5]")}`}
                    style={{ boxShadow: p !== currentPage ? "0 0 0 1px rgba(186,201,204,0.12)" : "none" }}>
                    {p}
                  </button>
                )
              )}

            <button data-testid="pagination-next" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-30 disabled:cursor-not-allowed ${dm ? "bg-[#1a2122] text-slate-300 hover:bg-[#252e2f]" : "bg-white text-[#3b494c] hover:bg-[#f3f4f5]"}`}
              style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12)" }}>Next →</button>

            <span className={`category-tag ml-2 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Page {currentPage} of {totalPages}</span>
          </div>
        )}
      </main>

      {/* Footer */}
      {/* Delete confirmation — the ONLY path to deleting a story (redesign item 2) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-story-dialog" className={dm ? "bg-[#1a2122] border-white/10 text-slate-100" : ""}>
          <DialogHeader>
            <DialogTitle className={`editorial-heading ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Delete this story?</DialogTitle>
            <DialogDescription className={dm ? "text-slate-400" : ""}>
              It will be removed for <strong>all users</strong> and from the archive. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <p className={`text-sm font-medium leading-snug px-3 py-2 rounded-lg ${dm ? "bg-white/5 text-slate-300" : "bg-[#f3f4f5] text-[#3b494c]"}`}>
              {deleteTarget.title}
            </p>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <button
              data-testid="delete-story-cancel-btn"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}
            >
              Cancel
            </button>
            <button
              data-testid="delete-story-confirm-btn"
              onClick={confirmDelete}
              disabled={deleting}
              className="px-4 py-2 rounded-full text-xs font-semibold text-white transition-colors disabled:opacity-60"
              style={{ background: "#b3362b" }}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <PublicPageFooter dark={dm} />
    </div>
  );
};

export default FeedPage;
